'use client'
import {
  init as initApm,
  apm,
  ApmBase,
  AgentConfigOptions,
} from '@elastic/apm-rum'
import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { OtelAttributes } from '@/lib/observability'
import type { SessionCurrentResponse } from '@/app/api/session/current/route'

interface ErrorPayload {
  exception?: { message?: string }
  log?: { message?: string }
}

/**
 * Extended config options that include features not in the type definitions
 * These are valid options per Elastic RUM docs but may not be in the TS types
 */
type ExtendedAgentConfigOptions = AgentConfigOptions & {
  monitorLongtasks?: boolean
  propagateTracestate?: boolean
  captureBody?: 'off' | 'errors' | 'transactions' | 'all'
}

interface RumProps {
  environment: string
  serviceVersion: string
  apmPath: string
  sampleRate?: number
  enabled?: boolean
}

function getTempSessionId(): string | undefined {
  const cookieMatch = /temp_session_id=([^;]+)/.exec(document.cookie)
  return cookieMatch?.[1]
}

function clearTempCookie() {
  document.cookie =
    'temp_session_id=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'
}

// ============================== ERROR FILTERING ==========================
/**
 * Noisy errors that should be filtered from APM - Browser Errors I cant Use
 */
const FILTERED_ERROR_PATTERNS = [
  'ResizeObserver loop',
  'ResizeObserver loop completed with undelivered notifications',
  'NetworkError when attempting to fetch resource',
  'Load failed',
]

function shouldFilterError(message: string | undefined): boolean {
  if (!message) return false
  return FILTERED_ERROR_PATTERNS.some((pattern) => message.includes(pattern))
}

function setupErrorFilter(apmInstance: ApmBase) {
  apmInstance.addFilter((payload) => {
    if (payload.errors) {
      payload.errors = payload.errors.filter((error: ErrorPayload) => {
        const message = error.exception?.message ?? error.log?.message
        return !shouldFilterError(message)
      })
      // If all errors were filtered, don't send the payload
      if (payload.errors.length === 0) {
        return false
      }
    }
    return payload
  })
}

// ============================ FORM ID ================================
/**
 * Extract form ID from current URL path
 */
function extractFormIdFromPath(pathname: string): string | null {
  // Match /forms/{formId} pattern
  const formMatch = /\/forms\/([a-zA-Z0-9-]+)/.exec(pathname)
  if (formMatch?.[1]) {
    return formMatch[1]
  }
  return null
}

/**
 * Setup transaction observer to enrich transactions with form context
 */
function setupTransactionObserver() {
  apm.observe('transaction:end', (transaction) => {
    const formId = extractFormIdFromPath(globalThis.location.pathname)
    if (formId) {
      transaction.addLabels({ [OtelAttributes.Form.ID]: formId })
    }
  })
}

/**
 * Check for backend session ID and correlate when detected
 * Also sets user context for APM when user is authenticated
 */
async function correlateTempAndLoggedInId(): Promise<boolean> {
  const tempSessionId = getTempSessionId()
  try {
    const res = await fetch('/api/session/current')
    if (!res.ok) return false

    const data: SessionCurrentResponse = await res.json()
    const { sessionId: loggedInSessionId, user } = data

    if (!loggedInSessionId) {
      if (tempSessionId) {
        apm.addLabels({ [OtelAttributes.Session.TEMP_ID]: tempSessionId })
      }
      return false
    }

    // Backend session detected - correlate temp -> backend mapping
    apm.addLabels({ [OtelAttributes.Session.ID]: loggedInSessionId })

    // Set user context for APM if user data is available
    if (user) {
      apm.setUserContext({
        id: user.id,
        username: user.name,
      })
      apm.addLabels({
        [OtelAttributes.User.ROLES]: user.roles.join(','),
        [OtelAttributes.User.BRANCH]: user.branch ?? '',
      })
    }

    try {
      await fetch('/api/session/correlate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tempSessionId,
          backendSessionId: loggedInSessionId,
        }),
      })
    } catch (err) {
      console.warn('Failed to send session correlation', err)
    }
    clearTempCookie()
    return true
  } catch (err) {
    console.warn('Failed to check for session ID', err)
    return false
  }
}

function getTrackerIdFromCookie(): string | undefined {
  const cookieMatch = /trackerId=([^;]+)/.exec(document.cookie)
  return cookieMatch?.[1]
}

function fetchTrackerId() {
  try {
    const trackerId = getTrackerIdFromCookie()
    if (trackerId) {
      apm.addLabels({ [OtelAttributes.Form.TRACKER_ID]: trackerId })
      return true
    }
    return false
  } catch (err) {
    console.warn('Failed to read tracker ID from cookie', err)
    return false
  }
}

export default function Rum({
  environment,
  serviceVersion,
  apmPath,
  sampleRate = 0.1,
  enabled = true,
}: RumProps) {
  const pathname = usePathname()
  const hasBackendSessionRef = useRef(false)
  const isInitializedRef = useRef(false)

  // Initialize APM on mount
  useEffect(() => {
    // Skip RUM if not enabled (e.g., in dev mode unless DEV_OTEL_TESTING=true)
    if (!enabled || !globalThis.location.origin) {
      return
    }

    // Prevent double initialization
    if (isInitializedRef.current) {
      return
    }
    isInitializedRef.current = true

    // Production: use origin + apmPath, Development: use localhost
    const serverUrl =
      process.env.NODE_ENV === 'production'
        ? `${globalThis.location.origin}/${apmPath}`
        : 'http://localhost:8200'

    const config: ExtendedAgentConfigOptions = {
      serviceName: 'frontend',
      serverUrl,
      serviceVersion,
      breakdownMetrics: true,
      apiVersion: 3,
      transactionSampleRate: sampleRate,
      environment,
      distributedTracingOrigins: [globalThis.location.origin],
      // Performance monitoring enhancements
      monitorLongtasks: true,
      // Distributed tracing - propagate sampling decision to backend
      propagateTracestate: true,
      // Capture request body for errors (helps debug failed form submissions)
      captureBody: 'errors',
    }

    const apmInstance = initApm(config as AgentConfigOptions)

    // Setup error filtering to reduce noise
    if (apmInstance) {
      setupErrorFilter(apmInstance)
    }

    // Setup transaction observer for form context enrichment
    setupTransactionObserver()
  }, [enabled, environment, serviceVersion, sampleRate, apmPath])

  // Check if User is logged in on route changes - Stop as soon as user is logged in
  useEffect(() => {
    // Skip if already have backend session or RUM disabled
    if (!enabled || hasBackendSessionRef.current) {
      return
    }

    // Mark that we have backend session (stop checking)
    correlateTempAndLoggedInId().then(
      (isLoggedIn) => (hasBackendSessionRef.current = isLoggedIn)
    )

    //fetch trackerID and set it apm add labels
    fetchTrackerId()
  }, [pathname, enabled])

  return null
}
